import { Box } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { dateToString } from 'modules/core/lib/dates'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import AddShiftContextualButton from 'modules/shift_management/components/ShiftManagement/AddShiftContextualButton'
import DropItem from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CellActions/EmptyCellActions'
import BaseShift from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Cells/ShiftCell/BaseShift'
import WrapperCell from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Cells/WrapperCell'
import EmptyShiftCell from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/EmptyShiftCell'
import { useDropShift } from 'modules/shift_management/hooks/useDragAndDrop'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'
import type { EmployeeWithShifts, Shift, ShiftsRow, View } from 'modules/shift_management/lib/types'
import { overrideGammaDimensions } from 'modules/shift_management/store/internal/models/View'
import type PlannedBreak from 'modules/time_planning/store/internal/models/PlannedBreak'

interface Props {
  view: View
  employee: EmployeeWithShifts
  shiftsRow: ShiftsRow
  shift: Shift
  preview: boolean
  previewPlannedBreaks?: PlannedBreak[] | null
  contractVersions: ContractVersions
}

const DailyShiftCell = ({
  view,
  employee,
  shiftsRow,
  shift,
  preview,
  previewPlannedBreaks = null,
  contractVersions,
}: Props) => {
  const { columnMinWidth, date } = view
  const { canManage } = employee

  // Calculate padding right and left to simulate partial hours in the grid
  const dimensions = overrideGammaDimensions()
  const minWidth = dimensions[columnMinWidth as keyof typeof dimensions]
  const paddingLeft = `calc(${minWidth} * ${shift.leftOffset(date)})`
  const paddingRight = `calc(${minWidth} * ${shift.rightOffset(date)})`

  const { isOver, canDrop, drop } = useDropShift({
    shiftsRow,
    date,
    shift,
  })

  const { daysByRow, showEmptyShiftCell } = useSelectionState()

  const hourId = dateToString(shift.date, view.dateFormat)
  const isSelected = (daysByRow[shiftsRow.key] || []).includes(hourId)

  return (
    <WrapperCell minWidth={columnMinWidth} padding="s4" ref={drop}>
      <Box gap="s4">
        {/* Auxiliar box to simulate partial hours in the grid with custom padding */}
        <Box style={{ paddingLeft, paddingRight }}>
          <BaseShift
            date={shift.date}
            shiftsRow={shiftsRow}
            shift={shift}
            employee={employee}
            isNightShift={shift.isEndNightShift(date)}
            preview={preview}
            previewPlannedBreaks={previewPlannedBreaks}
            contractVersions={contractVersions}
          />
          {canManage && (
            <AddShiftContextualButton
              shiftsRow={shiftsRow}
              date={shift.date}
              contractVersions={contractVersions}
            />
          )}
        </Box>
        {showEmptyShiftCell && isSelected && (
          <EmptyShiftCell employee={employee} contractVersions={contractVersions} />
        )}
        {canDrop && isOver && (
          <DropItem
            date={shift.date}
            shiftsRow={shiftsRow}
            isOver={isOver}
            contractVersions={contractVersions}
          />
        )}
      </Box>
    </WrapperCell>
  )
}

export default observer(DailyShiftCell)
